/**
 * Global Property Details Modal
 * 
 * Detailed view of a single global property showing definition,
 * validation rules, dependencies, state availability, and where-used analysis.
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Tree,
  Table,
  Tabs,
  Space,
  Typography,
  Spin,
  message,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { GlobalPropertiesService } from '../../services/api/globalPropertiesService';
import {
  PropertyDefinitionResponse,
  PropertyDependenciesResponse,
  PropertyValidationRulesResponse,
  PropertyStateAvailabilityResponse,
  PropertyDefinition,
  ValidationRules,
  CalculationDependencies,
  UserInputParameters,
  StateActions,
  StateActionEntry
} from '../../types/globalProperties';
import { apiClient } from '../../services/apiClient';
import { StateFlowEditor } from './StateFlowEditor';
import { Form, Input, Select, InputNumber } from 'antd';

const { Title, Text, Paragraph } = Typography;

interface GlobalPropertyDetailsModalProps {
  propertyKey: string;
  visible: boolean;
  onClose: () => void;
}

interface WhereUsedItem {
  table: string;
  artifact_id?: string;
  artifact_name?: string;
  property_id?: string;
  context?: string;
}

export const GlobalPropertyDetailsModal: React.FC<GlobalPropertyDetailsModalProps> = ({
  propertyKey,
  visible,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [definition, setDefinition] = useState<PropertyDefinition | null>(null);
  const [dependencies, setDependencies] = useState<PropertyDependenciesResponse | null>(null);
  const [validationRules, setValidationRules] = useState<ValidationRules | null>(null);
  const [stateAvailability, setStateAvailability] = useState<PropertyStateAvailabilityResponse | null>(null);
  const [whereUsed, setWhereUsed] = useState<WhereUsedItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [stateActionsForm] = Form.useForm();
  const [userInputForm] = Form.useForm();

  useEffect(() => {
    if (visible && propertyKey) {
      loadPropertyDetails();
    }
  }, [visible, propertyKey]);

  const loadPropertyDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load property definition
      const defResponse = await GlobalPropertiesService.getPropertyDefinition(propertyKey);
      setDefinition(defResponse.definition);

      // Load dependencies
      try {
        const depResponse = await GlobalPropertiesService.getPropertyDependencies(propertyKey);
        setDependencies(depResponse);
      } catch (err) {
        console.warn('Failed to load dependencies:', err);
      }

      // Load validation rules
      try {
        const valResponse = await GlobalPropertiesService.getPropertyValidationRules(propertyKey);
        setValidationRules(valResponse.validation_rules);
      } catch (err) {
        console.warn('Failed to load validation rules:', err);
      }

      // Load state availability
      try {
        const stateResponse = await GlobalPropertiesService.getPropertyStateAvailability(propertyKey);
        setStateAvailability(stateResponse);
      } catch (err) {
        console.warn('Failed to load state availability:', err);
      }

      // Load where-used information
      await loadWhereUsed();
    } catch (err: any) {
      setError(err.message || 'Failed to load property details');
      message.error('Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const loadWhereUsed = async () => {
    try {
      // Query different property tables to find where this property is used
      const whereUsedItems: WhereUsedItem[] = [];

      // Check prime artifact properties
      try {
        const response = await apiClient.get('/prime-artifacts', { params: { skip: 0, limit: 1000 } });
        const artifacts = Array.isArray(response.data) ? response.data : (response.data?.items || []);
        
        for (const artifact of artifacts) {
          try {
            const propsResponse = await apiClient.get(`/prime-artifacts/${artifact.id}/properties`);
            const properties = Array.isArray(propsResponse.data) ? propsResponse.data : (propsResponse.data?.properties || []);
            const matchingProps = properties.filter((p: any) => p.property_key === propertyKey);
            matchingProps.forEach((prop: any) => {
              whereUsedItems.push({
                table: 'prime_artifact_property',
                artifact_id: artifact.id,
                artifact_name: artifact.name,
                property_id: prop.id,
                context: 'Prime Artifact'
              });
            });
          } catch (err) {
            // Skip if can't load properties for this artifact
          }
        }
      } catch (err) {
        console.warn('Failed to check prime artifacts:', err);
      }

      // Check state template properties
      try {
        const response = await apiClient.get('/state-templates', { params: { skip: 0, limit: 1000 } });
        const templates = Array.isArray(response.data) ? response.data : (response.data?.items || []);
        
        for (const template of templates) {
          try {
            const propsResponse = await apiClient.get(`/state-templates/${template.id}/property-mappings`);
            const properties = Array.isArray(propsResponse.data) ? propsResponse.data : [];
            const matchingProps = properties.filter((p: any) => p.property_key === propertyKey);
            matchingProps.forEach((prop: any) => {
              whereUsedItems.push({
                table: 'state_template_property_mapping',
                artifact_id: template.id,
                artifact_name: template.name,
                property_id: prop.id,
                context: 'State Template'
              });
            });
          } catch (err) {
            // Skip if can't load properties for this template
          }
        }
      } catch (err) {
        console.warn('Failed to check state templates:', err);
      }

      setWhereUsed(whereUsedItems);
    } catch (err) {
      console.warn('Failed to load where-used information:', err);
    }
  };

  const buildDependencyTree = (): any[] => {
    if (!definition?.calculation_dependencies?.source_properties) return [];

    return Object.entries(definition.calculation_dependencies.source_properties).map(([key, dep]) => ({
      title: (
        <Space>
          <Text strong>{dep.property_key}</Text>
          {dep.source && <Tag>{dep.source}</Tag>}
          {dep.description && <Text type="secondary">({dep.description})</Text>}
        </Space>
      ),
      key: dep.property_key
    }));
  };

  const whereUsedColumns = [
    {
      title: 'Table',
      dataIndex: 'table',
      key: 'table',
      render: (text: string) => <Tag>{text}</Tag>
    },
    {
      title: 'Context',
      dataIndex: 'context',
      key: 'context'
    },
    {
      title: 'Artifact Name',
      dataIndex: 'artifact_name',
      key: 'artifact_name'
    },
    {
      title: 'Property ID',
      dataIndex: 'property_id',
      key: 'property_id',
      render: (id: string) => id ? <Text code>{id.substring(0, 8)}...</Text> : '-'
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Property: {propertyKey}</Title>
          {definition && (
            <Tag color="blue">{definition.property_type}</Tag>
          )}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
    >
      <Spin spinning={loading}>
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {definition && (
          <Tabs 
            defaultActiveKey="definition"
            items={[
              {
                key: 'definition',
                label: 'Definition',
                children: (
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Property Key">
                  <Text code>{definition.property_key}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Property Type">
                  <Tag color="blue">{definition.property_type}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Description">
                  {definition.description || <Text type="secondary">No description</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Input Source">
                  {definition.input_source || <Text type="secondary">Not specified</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Default Value">
                  {definition.default_value !== undefined && definition.default_value !== null
                    ? String(definition.default_value)
                    : <Text type="secondary">No default value</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Option Catalog">
                  {definition.global_option_catalog_ref ? (
                    <Tag color="cyan">{definition.global_option_catalog_ref}</Tag>
                  ) : (
                    <Text type="secondary">No option catalog</Text>
                  )}
                </Descriptions.Item>
              </Descriptions>
                )
              },
              {
                key: 'validation',
                label: 'Validation Rules',
                children: (
                  <>
                    {validationRules ? (
                      <Descriptions column={1} bordered>
                        {Object.entries(validationRules).map(([key, value]) => (
                          <Descriptions.Item key={key} label={key}>
                            {typeof value === 'boolean' ? (
                              value ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                            ) : (
                              String(value)
                            )}
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    ) : (
                      <Text type="secondary">No validation rules defined</Text>
                    )}
                  </>
                )
              },
              {
                key: 'dependencies',
                label: 'Dependencies',
                children: (
                  <>
                    {definition.calculation_dependencies ? (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Text strong>Calculation Type: </Text>
                        <Tag color="purple">{definition.calculation_dependencies.type}</Tag>
                        {definition.calculation_dependencies.source_properties && (
                          <>
                            <Title level={5}>Source Properties:</Title>
                            <Tree
                              treeData={buildDependencyTree()}
                              defaultExpandAll
                            />
                          </>
                        )}
                      </Space>
                    ) : (
                      <Text type="secondary">No calculation dependencies</Text>
                    )}
                  </>
                )
              },
              {
                key: 'states',
                label: 'States',
                children: (
                  <>
                    {definition.state_actions ? (
                      <Form form={stateActionsForm} layout="vertical">
                        <Form.Item name="state_actions" initialValue={definition.state_actions}>
                          <StateFlowEditor
                            value={Array.isArray(definition.state_actions) 
                              ? (() => {
                                  // Convert array to nested format for StateFlowEditor
                                  const result: StateActions = { operational: {}, improvement: {} };
                                  (definition.state_actions as StateActionEntry[]).forEach(entry => {
                                    if (entry.flow_type === 'operational') {
                                      result.operational![entry.state] = entry.state_action_type;
                                    } else if (entry.flow_type === 'improvement') {
                                      result.improvement![entry.state] = entry.state_action_type;
                                    }
                                  });
                                  return result;
                                })()
                              : (definition.state_actions as StateActions)}
                            editable={false}
                          />
                        </Form.Item>
                      </Form>
                    ) : (
                      <Text type="secondary">No state actions configured</Text>
                    )}
                  </>
                )
              },
              {
                key: 'user-input-parameters',
                label: 'User Input Parameters',
                children: (
                  <>
                    {definition.user_input_parameters ? (
                      <Form form={userInputForm} layout="vertical" initialValues={definition.user_input_parameters}>
                        <Form.Item label="Input Type" name="input_type">
                          <Select disabled>
                            <Select.Option value="text">Text</Select.Option>
                            <Select.Option value="number">Number</Select.Option>
                            <Select.Option value="select">Select</Select.Option>
                            <Select.Option value="date">Date</Select.Option>
                            <Select.Option value="checkbox">Checkbox</Select.Option>
                            <Select.Option value="textarea">Textarea</Select.Option>
                          </Select>
                        </Form.Item>
                        <Form.Item label="Label" name="label">
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label="Placeholder" name="placeholder">
                          <Input disabled />
                        </Form.Item>
                        <Form.Item label="Help Text" name="help_text">
                          <Input.TextArea rows={3} disabled />
                        </Form.Item>
                        {definition.user_input_parameters.input_type === 'select' && definition.user_input_parameters.options && (
                          <Form.Item label="Options">
                            <Space direction="vertical" style={{ width: '100%' }}>
                              {definition.user_input_parameters.options.map((option, index) => (
                                <Space key={index}>
                                  <Tag>{option.label}</Tag>
                                  <Text type="secondary">({option.value})</Text>
                                </Space>
                              ))}
                            </Space>
                          </Form.Item>
                        )}
                        {definition.user_input_parameters.validation && (
                          <Form.Item label="Validation Rules">
                            <Descriptions column={1} bordered size="small">
                              {Object.entries(definition.user_input_parameters.validation).map(([key, value]) => (
                                <Descriptions.Item key={key} label={key}>
                                  {typeof value === 'boolean' ? (
                                    value ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                                  ) : (
                                    String(value)
                                  )}
                                </Descriptions.Item>
                              ))}
                            </Descriptions>
                          </Form.Item>
                        )}
                      </Form>
                    ) : (
                      <Text type="secondary">No user input parameters configured</Text>
                    )}
                  </>
                )
              },
              {
                key: 'where-used',
                label: 'Where Used',
                children: (
                  <Table
                    columns={whereUsedColumns}
                    dataSource={whereUsed.map((item, index) => ({ 
                      ...item, 
                      _rowKey: `${item.table}-${item.artifact_id || 'no-artifact'}-${item.property_id || `item-${index}`}` 
                    }))}
                    rowKey="_rowKey"
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: 'This property is not referenced in any property tables' }}
                  />
                )
              }
            ]}
          />
        )}

        {!definition && !loading && (
          <Alert
            message="Property Not Found"
            description={`Property definition for "${propertyKey}" could not be found.`}
            type="warning"
            showIcon
          />
        )}
      </Spin>
    </Modal>
  );
};

